import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import InfoTooltip from '../../../components/ui/Tooltip';
import { supabase } from '../../../lib/supabase';
import propertyService from '../../../services/propertyService';
import usageTrackingService from '../../../services/usageTracking';
import { stripeService } from '../../../services/stripe';

/**
 * Stress tests commentés par OpenAI (Pro uniquement).
 * Scénarios: taux +2%, vacance +5%, revenus -10%, combiné défavorable.
 */
const StressTestsAI = ({
  property,
  user,
  userProfile,
  subscriptionTier = 'free',
  onUpgrade,
}) => {
  const [stressData, setStressData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [error, setError] = useState(null);
  const [usageStats, setUsageStats] = useState(null);

  const limits = stripeService?.getTierLimits(subscriptionTier);
  const isPro = true;

  useEffect(() => {
    if (!isPro || !property?.id || !user?.id) {
      setLoadingExisting(false);
      return;
    }
    const load = async () => {
      try {
        const { data: rows } = await supabase
          .from('property_ai_analyses')
          .select('analysis_data')
          .eq('property_id', property.id)
          .eq('user_id', user.id)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(20);
        const payload = (rows || []).find((r) => r?.analysis_data?.type === 'stress_tests')?.analysis_data;
        if (payload?.scenarios) {
          setStressData(payload);
        }
      } catch (e) {
        console.error('Load stress tests:', e);
      } finally {
        setLoadingExisting(false);
      }
    };
    load();
  }, [isPro, property?.id, user?.id]);

  useEffect(() => {
    if (!user?.id || !isPro) return;
    usageTrackingService.getUsageStats(user.id).then((s) => setUsageStats(s));
  }, [user?.id, isPro]);

  const handleGenerate = async () => {
    if (!isPro) {
      onUpgrade?.();
      return;
    }
    if (!property || !user?.id) return;
    const aiLimit = usageStats?.aiAnalyses?.limit;
    const aiUsed = usageStats?.aiAnalyses?.used ?? 0;
    if (aiLimit !== '∞' && typeof aiLimit === 'number' && aiUsed >= aiLimit) {
      setError(`Vous avez atteint la limite de ${aiLimit} interactions IA ce mois.`);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('stress-tests-ai', {
        body: {
          propertyData: property,
          userId: user.id,
          investorProfile: userProfile || null,
        },
      });
      if (fnError) throw new Error(fnError.message || 'Erreur lors de l\'appel');
      if (!data?.success || !data?.data) throw new Error(data?.error || 'Aucun résultat');
      const payload = {
        type: 'stress_tests',
        scenarios: data.data.scenarios,
        resume: data.data.resume,
        metrics: data.data.metrics,
        marges: data.data.marges || null,
      };
      setStressData(payload);
      await supabase.from('property_ai_analyses').insert({
        property_id: property.id,
        user_id: user.id,
        analysis_data: payload,
      });
      if (user?.id) {
        const s = await usageTrackingService.getUsageStats(user.id);
        setUsageStats(s);
      }
    } catch (err) {
      console.error('Stress tests error:', err);
      setError(err?.message || 'Erreur lors de la génération des stress tests');
    } finally {
      setLoading(false);
    }
  };

  if (!isPro) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <Icon name="Activity" size={48} className="mx-auto mb-4 text-primary" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Stress tests commentés</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          Taux d&apos;intérêt +2%, vacance +5%, baisse des revenus, scénario combiné défavorable — avec commentaires IA. Réservé au plan Pro.
        </p>
        <Button variant="default" iconName="Crown" onClick={onUpgrade}>
          Passer au plan Pro
        </Button>
      </div>
    );
  }

  if (loadingExisting) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 flex items-center justify-center gap-2">
        <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">Chargement...</span>
      </div>
    );
  }

  if (!stressData && !loading && !error) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <Icon name="Activity" size={48} className="mx-auto mb-4 text-primary" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Stress tests commentés (IA)</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          Marges réelles (taux, vacance, revenus) calculées pour cette propriété, puis commentaires IA sur le niveau de risque.
        </p>
        {usageStats?.aiAnalyses?.limit !== '∞' && (
          <p className="text-sm text-muted-foreground mb-4">
            Utilisation IA ce mois : {usageStats?.aiAnalyses?.used ?? 0} / {usageStats?.aiAnalyses?.limit ?? 0}
          </p>
        )}
        <Button
          variant="default"
          iconName={loading ? 'Loader2' : 'Sparkles'}
          onClick={handleGenerate}
          disabled={loading || (usageStats?.aiAnalyses?.limit !== '∞' && (usageStats?.aiAnalyses?.used ?? 0) >= (usageStats?.aiAnalyses?.limit ?? 0))}
        >
          {loading ? 'Génération...' : 'Générer les stress tests'}
        </Button>
      </div>
    );
  }

  if (error && !stressData) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 text-center">
        <Icon name="AlertCircle" size={40} className="mx-auto mb-3 text-destructive" />
        <p className="text-destructive mb-4">{error}</p>
        <Button variant="outline" iconName="RefreshCw" onClick={handleGenerate} disabled={loading}>
          Réessayer
        </Button>
      </div>
    );
  }

  const marges = stressData?.marges;

  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm relative">
      {/* En-tête */}
      <div className="px-5 py-4 border-b border-border bg-gradient-to-r from-primary/5 to-primary/10 flex items-center justify-between flex-wrap gap-2 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-primary/10">
            <Icon name="Activity" size={22} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Stress tests commentés</h3>
            <p className="text-xs text-muted-foreground">Marges réelles et analyse IA</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" iconName="RefreshCw" onClick={handleGenerate} disabled={loading}>
          {loading ? 'Génération...' : 'Régénérer'}
        </Button>
      </div>

      {/* Bloc marges (si présentes) */}
      {marges && (
        <div className="px-5 py-4 border-b border-border bg-muted/30">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Shield" size={16} className="text-primary" />
            Marges de la propriété (seuil cashflow = 0)
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="flex items-center gap-1 mb-1">
                <Icon name="TrendingUp" size={18} className="text-amber-500" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Taux d&apos;intérêt</span>
                <InfoTooltip content="Hausse maximale du taux d'intérêt que l'immeuble peut supporter avant que le cashflow ne devienne négatif." />
              </div>
              <p className="text-2xl font-bold text-foreground">+{marges.marge_taux_pp ?? 0} pt</p>
              <p className="text-xs text-muted-foreground mt-0.5">Hausse max avant cashflow nul</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="flex items-center gap-1 mb-1">
                <Icon name="Home" size={18} className="text-emerald-500" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Vacance</span>
                <InfoTooltip content="Taux d'inoccupation maximal que l'immeuble peut supporter avant d'être en déficit mensuel." />
              </div>
              <p className="text-2xl font-bold text-foreground">+{marges.marge_vacance_pp ?? 0} pt</p>
              <p className="text-xs text-muted-foreground mt-0.5">Augmentation max avant cashflow nul</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="flex items-center gap-1 mb-1">
                <Icon name="DollarSign" size={18} className="text-blue-500" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Revenus</span>
                <InfoTooltip content="Baisse maximale des revenus (loyers) permise avant que l'opération ne soit plus rentable mensuellement." />
              </div>
              <p className="text-2xl font-bold text-foreground">−{marges.marge_revenus_pct ?? 0} %</p>
              <p className="text-xs text-muted-foreground mt-0.5">Baisse max des revenus avant cashflow nul</p>
            </div>
          </div>
        </div>
      )}

      {/* Résumé IA */}
      {stressData?.resume && (
        <div className="px-5 py-4 border-b border-border bg-primary/5">
          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            <Icon name="MessageSquare" size={16} className="text-primary" />
            Synthèse
          </h4>
          <p className="text-sm leading-relaxed text-foreground">{stressData.resume}</p>
        </div>
      )}

      {/* Scénarios commentés */}
      <div className="px-5 py-4">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="FileText" size={16} className="text-primary" />
          Scénarios et commentaires
        </h4>
        <div className="space-y-4">
          {(stressData?.scenarios || []).map((s, i) => (
            <div
              key={s.id ?? i}
              className="rounded-xl border border-border bg-card p-4 shadow-sm hover:border-primary/30 transition-colors"
            >
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary text-xs font-bold">
                  {i + 1}
                </span>
                {s.nom}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed pl-9">{s.commentaire}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StressTestsAI;
